import { EditorState, Plugin } from 'prosemirror-state';
import { Mark, Node } from 'prosemirror-model';
import { Transform } from 'prosemirror-transform';

type TMarkDictionary = Record<string, number | string>;

function applyAlignmentAcrossPositions(
  state,
  transaction,
  from,
  to,
  alignment,
  mergeAttrs: {}
) {
  state.doc.nodesBetween(from, to, (node, pos, _parent, _index) => {
    if (node.type.name !== 'paragraph') return;

    transaction.setNodeMarkup(pos, null, {
      ...node.attrs,
      ...mergeAttrs,
      'text-align': alignment
    });
  });
}

function applyMarksToParagraph(
  marks: Array<Mark>,
  newState: EditorState,
  newTransaction: Transform,
  paragraphPosition: number,
  paragraphContentLength: number
) {
  applyMarksAcrossPositions(
    marks,
    newState,
    newTransaction,
    paragraphPosition + 1,
    paragraphPosition + 1 + paragraphContentLength
  );
}

function applyMarksAcrossPositions(
  marks: Array<Mark>,
  newState: EditorState,
  newTransaction: Transform,
  from: number,
  to: number
) {
  marks.forEach((mark) => {
    newTransaction.addMark(from, to, mark);
  });
}

function cacheMarksOnParagraph(
  newTransaction: Transform,
  paragraphPosition: number,
  currentAttrs: Record<string, any>,
  oldMarks: Array<Mark>
) {
  newTransaction.setNodeMarkup(paragraphPosition, null, {
    ...currentAttrs,
    marks: marksToDictionary(oldMarks)
  });
}

function defaultMarksAtPosition(state: EditorState, position: number) {
  const resolvedPosition = state.doc.resolve(position);
  const paragraphSurroundingPosition = resolvedPosition.node(1);
  const markValues = paragraphSurroundingPosition.attrs.marks;

  return markValues || {};
}

function dictionaryToMarks(state: EditorState, dictionary: TMarkDictionary) {
  const marks = [];

  Object.keys(dictionary).forEach((markName) => {
    const schemaMark = state.schema.marks[markName];
    marks.push(schemaMark.create({ [markName]: dictionary[markName] }));
  });

  return Mark.setFrom(marks);
}

function marksAtPosition(state, position, defaultMarks: TMarkDictionary = {}) {
  const selectionMarks = marksToDictionary(state.doc.resolve(position).marks());
  const storedMarks = marksToDictionary(state.storedMarks || []);

  return dictionaryToMarks(state, {
    ...defaultMarks,
    ...selectionMarks,
    ...storedMarks
  });
}

function marksToDictionary(marks: Array<Mark>) {
  const dictionary = {};

  marks.forEach((mark) => {
    const markName = mark.type.name;
    dictionary[markName] = mark.attrs[markName];
  });

  return dictionary;
}

function removeCachedMarksFromParagraph(
  newTransaction: Transform,
  paragraphPosition: number,
  paragraph: Node
) {
  newTransaction.setNodeMarkup(
    paragraphPosition,
    null,
    { ...paragraph.attrs, marks: null },
    null
  );
}

function handleDeletedParagraph(
  oldState: EditorState,
  newState: EditorState,
  newTransaction: Transform
) {
  if (oldState.selection.from !== oldState.selection.to) {
    const paragraph = newState.selection.$from.node(1);
    const paragraphPosition = newState.selection.$from.before(1);
    const oldParagraph = oldState.doc.nodeAt(paragraphPosition);

    if (paragraph.content.size === 0) {
      const marks = marksAtPosition(
        oldState,
        oldState.selection.from,
        defaultMarksAtPosition(oldState, oldState.selection.from)
      );

      cacheMarksOnParagraph(
        newTransaction,
        paragraphPosition,
        { ...paragraph.attrs, 'text-align': oldParagraph.attrs['text-align'] },
        marks
      );

      return newTransaction;
    }
  }

  return null;
}

function handleNewParagraph(
  oldState: EditorState,
  newState: EditorState,
  newTransaction: Transform
) {
  const oldParagraphAtSelectionFrom = oldState.selection.$from.node(1);
  const newParagraphAtOldSelectionFrom = newState.doc
    .resolve(oldState.selection.from)
    .node(1);
  const paragraphPositionAtOldSelectionFrom = newState.doc
    .resolve(oldState.selection.from)
    .before(1);

  const paragraphAtNewSelectionFrom = newState.selection.$from.node(1);
  const paragraphPositionAtNewSelectionFrom = newState.selection.$from.before(
    1
  );

  const alignment = oldState.selection.$head.node(1).attrs['text-align'];
  const marks = marksAtPosition(
    oldState,
    oldState.selection.head,
    defaultMarksAtPosition(oldState, oldState.selection.head)
  );

  if (
    oldParagraphAtSelectionFrom.childCount > 0 &&
    newParagraphAtOldSelectionFrom.childCount === 0
  ) {
    cacheMarksOnParagraph(
      newTransaction,
      paragraphPositionAtOldSelectionFrom,
      { ...oldParagraphAtSelectionFrom.attrs, 'text-align': alignment },
      marks
    );
  } else if (paragraphAtNewSelectionFrom.childCount === 0) {
    cacheMarksOnParagraph(
      newTransaction,
      paragraphPositionAtNewSelectionFrom,
      { ...oldParagraphAtSelectionFrom.attrs, 'text-align': alignment },
      marks
    );
  }

  return newTransaction.steps.length ? newTransaction : null;
}

function handleChangesWithinParagraph(
  oldState: EditorState,
  newState: EditorState,
  newTransaction: Transform
) {
  const paragraph = newState.selection.$head.node(1);
  const paragraphPosition = newState.selection.$head.before(1);
  const oldParagraph = oldState.doc.nodeAt(paragraphPosition);
  const paragraphContentLength = paragraph.content.size;

  if (oldParagraph.childCount === 0 && paragraph.childCount > 0) {
    const marks = marksAtPosition(
      oldState,
      oldState.selection.head,
      defaultMarksAtPosition(newState, newState.selection.head)
    );

    applyMarksToParagraph(
      marks,
      newState,
      newTransaction,
      paragraphPosition,
      paragraphContentLength
    );

    removeCachedMarksFromParagraph(
      newTransaction,
      paragraphPosition,
      paragraph
    );
  } else if (oldParagraph.childCount > 0 && paragraph.childCount === 0) {
    const oldMarks = marksAtPosition(oldState, oldState.selection.head, {});
    const currentAttrs = paragraph.attrs;

    cacheMarksOnParagraph(
      newTransaction,
      paragraphPosition,
      currentAttrs,
      oldMarks
    );
  } else {
    return null;
  }

  return newTransaction;
}

function handleNoChangeToDocument(
  _newState: EditorState,
  _newTransaction: Transform
) {
  return null;
}

function hasDocumentChanged(transactions: Array<Transform>) {
  return transactions.some((transaction) => transaction.docChanged);
}

function userAddedParagraph(oldState, newState) {
  return oldState.doc.childCount < newState.doc.childCount;
}

function userRemovedParagraph(oldState, newState) {
  return oldState.doc.childCount > newState.doc.childCount;
}

function userChangedParagraphCount(oldState, newState) {
  return oldState.doc.childCount !== newState.doc.childCount;
}

function isDropping(transactions) {
  return transactions.some((tr) => tr.getMeta('uiEvent') === 'drop');
}

function isPasting(transactions) {
  return transactions.some((tr) => tr.getMeta('uiEvent') === 'paste');
}

function userHasSelectionAcrossParagraphs(oldState) {
  return !oldState.selection.$from.node(1).eq(oldState.selection.$to.node(1));
}

function textContentChanged(oldState, newState) {
  return oldState.doc.textContent !== newState.doc.textContent;
}

function userHasSelection(oldState) {
  return oldState.selection.from !== oldState.selection.to;
}

function userHasEntireParagraphSelected(oldState) {
  const fromNode = oldState.selection.$from.node(1);
  const nodeContentStartPos = oldState.selection.$from.before(1) + 1;

  return (
    userHasSelection(oldState) &&
    oldState.selection.from === nodeContentStartPos &&
    oldState.selection.to === nodeContentStartPos + fromNode.content.size
  );
}

function handleChangesAcrossMultipleParagraphs(
  transactions,
  oldState,
  newState,
  newTransaction
) {
  if (!textContentChanged(oldState, newState)) return null;

  const oldSelection = oldState.selection;
  let diffStart = oldState.doc.content.findDiffStart(newState.doc.content);

  const diffEnd = oldState.doc.content.findDiffEnd(newState.doc.content);

  const alignment = oldState.selection.$from.node(1).attrs['text-align'];
  const marks = marksAtPosition(
    oldState,
    oldSelection.from,
    defaultMarksAtPosition(oldState, oldSelection.from)
  );

  newState.doc.nodesBetween(
    diffStart,
    diffEnd.b,
    (node, pos, _parent, _index) => {
      if (node.type.name !== 'paragraph') return;

      if (node.childCount === 0) {
        cacheMarksOnParagraph(
          newTransaction,
          pos,
          { ...node.attrs, 'text-align': alignment },
          marks
        );
      } else {
        cacheMarksOnParagraph(
          newTransaction,
          pos,
          { ...node.attrs, 'text-align': alignment, marks: null },
          marks
        );
      }
    }
  );

  return newTransaction;
}

export function markHandling() {
  return new Plugin({
    appendTransaction(transactions, oldState, newState) {
      const newTransaction = newState.tr;

      if (isPasting(transactions)) return null;

      if (!hasDocumentChanged(transactions)) {
        return handleNoChangeToDocument(newState, newTransaction);
      } else if (
        userHasEntireParagraphSelected(oldState) ||
        userHasSelectionAcrossParagraphs(oldState)
      ) {
        return handleChangesAcrossMultipleParagraphs(
          transactions,
          oldState,
          newState,
          newTransaction
        );
      } else if (
        textContentChanged(oldState, newState) &&
        !userChangedParagraphCount(oldState, newState)
      ) {
        return handleChangesWithinParagraph(oldState, newState, newTransaction);
      } else if (userAddedParagraph(oldState, newState)) {
        return handleNewParagraph(oldState, newState, newTransaction);
      } else if (userRemovedParagraph(oldState, newState)) {
        return handleDeletedParagraph(oldState, newState, newTransaction);
      } else {
        return null;
      }
    }
  });
}

export function applyMarksToPastedContent() {
  return new Plugin({
    appendTransaction(transactions, oldState, newState) {
      const oldSelection = oldState.selection;

      if (
        hasDocumentChanged(transactions) &&
        (isPasting(transactions) || isDropping(transactions))
      ) {
        let diffStart = oldState.doc.content.findDiffStart(
          newState.doc.content
        );

        if (diffStart === null) return null;

        const diffEnd = oldState.doc.content.findDiffEnd(newState.doc.content);
        const oldParagraph = oldState.selection.$from.node(1);
        const newParagraph = newState.doc.resolve(oldSelection.from).node(1);
        const newParagraphPosition = newState.doc
          .resolve(oldSelection.from)
          .before(1);

        const alignment = oldParagraph.attrs['text-align'];
        const marks = marksAtPosition(
          oldState,
          oldSelection.from,
          defaultMarksAtPosition(oldState, oldSelection.from)
        );

        const newTransaction = newState.tr;
        applyMarksAcrossPositions(
          marks,
          newState,
          newTransaction,
          diffStart,
          diffEnd.b
        );

        applyAlignmentAcrossPositions(
          newState,
          newTransaction,
          diffStart,
          diffEnd.b,
          alignment
        );

        if (
          oldParagraph?.content?.size === 0 &&
          newParagraph?.content?.size > 0
        ) {
          removeCachedMarksFromParagraph(
            newTransaction,
            newParagraphPosition,
            newParagraph
          );
        }

        return newTransaction;
      }
      return null;
    }
  });
}
